/**
 * @file What a thrown weapon does on its way down (issue #15).
 *
 * Runs straight after the collision system, so the wall push-out and the
 * `touchedStatic` flag it sets are already this frame's truth. Three outcomes,
 * checked in that order:
 *
 *  1. it swept over an enemy  → stun that enemy, make it drop its own weapon,
 *     and destroy the throw (a weapon is spent on the first body it hits);
 *  2. it hit a wall or ran out of range → it drops where it is and becomes a
 *     normal floor pickup;
 *  3. neither → it keeps flying.
 *
 * The sweep only ever considers entities with enemy AI, so a throw can never
 * come back and hurt the player.
 */

import * as THREE from 'three';
import { System } from '../ecs/System.js';
import {
  Transform,
  Collider,
  Health,
  AIBrain,
  RangedAI,
  ThrownWeapon
} from '../components/index.js';
import { raycastAABB } from '../physics/AABB.js';
import { Events } from '../core/EventBus.js';
import { groundWeapon } from '../entities/WeaponDrop.js';
import { stunAndDisarm } from '../combat/stunDisarm.js';

export class ThrownWeaponSystem extends System {
  static priority = 51;

  constructor() {
    super();
    /** @type {THREE.Vector3} @private */
    this._hitPoint = new THREE.Vector3();
    /** @type {THREE.Vector3} @private */
    this._hitNormal = new THREE.Vector3();
    /** @type {object} @private */
    this._hitEvent = { position: this._hitPoint, normal: this._hitNormal, target: null };
    /** @type {number} Hit parameter along the swept segment, set by `_sweep`. @private */
    this._hitT = 0;
  }

  /**
   * @param {number} _dt Delta time in seconds.
   * @returns {void}
   */
  update(_dt) {
    const thrown = this.world.query(ThrownWeapon, Transform, Collider);
    if (thrown.length === 0) return;

    const enemies = this._enemyCandidates();

    for (const entity of thrown) {
      if (!entity.alive) continue;
      const marker = entity.get(ThrownWeapon);
      if (marker.grounded) continue;

      const transform = entity.get(Transform);
      const collider = entity.get(Collider);
      const from = transform.previousPosition;
      const dx = transform.position.x - from.x;
      const dz = transform.position.z - from.z;

      marker.travelled += Math.hypot(dx, dz);

      const target = this._sweep(entity, marker, collider, from, dx, dz, enemies);
      if (target) {
        this._impact(entity, marker, target, from, dx, dz);
        continue;
      }

      if (collider.touchedStatic || marker.travelled >= marker.range) groundWeapon(entity);
    }
  }

  /**
   * Nearest enemy along the segment the weapon covered this frame. Swept rather
   * than tested at the end position, so a throw cannot skip past a thin enemy
   * on a slow frame.
   *
   * @param {import('../ecs/Entity.js').Entity} entity Thrown weapon.
   * @param {import('../components/ThrownWeapon.js').ThrownWeapon} marker Its flight state.
   * @param {import('../components/Collider.js').Collider} collider Its collider.
   * @param {THREE.Vector3} from Position at the start of the frame.
   * @param {number} dx Motion along X.
   * @param {number} dz Motion along Z.
   * @param {import('../ecs/Entity.js').Entity[]} enemies Candidate targets.
   * @returns {import('../ecs/Entity.js').Entity | null}
   * @private
   */
  _sweep(entity, marker, collider, from, dx, dz, enemies) {
    let bestT = Infinity;
    /** @type {import('../ecs/Entity.js').Entity | null} */
    let best = null;

    for (const other of enemies) {
      if (other === entity || !other.alive || other.id === marker.ownerId) continue;
      if (other.get(Health)?.dead) continue;

      const otherCollider = other.get(Collider);
      const otherTransform = other.get(Transform);
      // Minkowski expansion: inflate the target by the weapon's own size so the
      // sweep can stay a zero-width ray.
      const t = raycastAABB(
        from.x,
        from.z,
        dx,
        dz,
        otherTransform.position.x - otherCollider.halfX - collider.halfX,
        otherTransform.position.z - otherCollider.halfZ - collider.halfZ,
        otherTransform.position.x + otherCollider.halfX + collider.halfX,
        otherTransform.position.z + otherCollider.halfZ + collider.halfZ
      );
      if (t < 0 || t > 1 || t >= bestT) continue;

      bestT = t;
      best = other;
    }

    this._hitT = best ? bestT : 0;
    return best;
  }

  /**
   * Stuns the enemy, knocks its weapon out of its hands and spends the throw.
   * No damage is dealt: the weapon buys time, it does not kill.
   *
   * @param {import('../ecs/Entity.js').Entity} entity Thrown weapon.
   * @param {import('../components/ThrownWeapon.js').ThrownWeapon} marker Its flight state.
   * @param {import('../ecs/Entity.js').Entity} target Enemy that was hit.
   * @param {THREE.Vector3} from Position at the start of the frame.
   * @param {number} dx Motion along X.
   * @param {number} dz Motion along Z.
   * @returns {void}
   * @private
   */
  _impact(entity, marker, target, from, dx, dz) {
    marker.hasHit = true;

    stunAndDisarm(this.ctx, target);

    // Impact FX and its sound both already hang off this event.
    const t = this._hitT;
    this._hitPoint.set(from.x + dx * t, entity.get(Transform).position.y, from.z + dz * t);
    this._hitNormal.set(-dx, 0, -dz).normalize();
    this._hitEvent.target = target;
    this.ctx.events.emit(Events.BULLET_HIT, this._hitEvent);

    this.world.destroyEntity(entity);
  }

  /**
   * @returns {import('../ecs/Entity.js').Entity[]} Every enemy AI a thrown weapon can stun.
   * @private
   */
  _enemyCandidates() {
    const classic = this.world.query(AIBrain, Transform, Collider);
    const ranged = this.world.query(RangedAI, Transform, Collider);
    if (classic.length === 0) return ranged;
    if (ranged.length === 0) return classic;

    const seen = new Set();
    const result = [];
    for (const entity of classic) {
      seen.add(entity.id);
      result.push(entity);
    }
    for (const entity of ranged) {
      if (seen.has(entity.id)) continue;
      result.push(entity);
    }
    return result;
  }
}
